import React, { useState, useEffect, useMemo } from "react";
import { ref, onValue, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Users,
  Filter,
  Download,
  Search,
  Calendar,
  DollarSign,
  TrendingUp,
  FileText,
  X,
} from "lucide-react";
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CLIENT_TRANSACTIONS_PAGE_SIZE_OPTIONS,
  DEFAULT_CLIENT_TRANSACTIONS_PAGE_SIZE,
  buildClientTransactionChartData,
  clientTransactionsRangeForPage,
  getClientTransactionTotalPages,
  paginateClientTransactions,
} from "./clientTransactionsInsights";

const PIE_COLORS = ["#06b6d4", "#22c55e", "#f59e0b", "#6366f1", "#ef4444", "#a855f7"];

/**
 * ClientTransactionsTab - Comprehensive client payment and transaction tracking
 * 
 * Features:
 * - List all clients with balances
 * - View detailed transaction history per client
 * - Track milk purchases (credit and paid)
 * - Track payments received
 * - Monthly transaction view with date filtering
 * - Export transaction statements (CSV/PDF)
 * - Integration with M-Pesa paybill payments
 */
const ClientTransactionsTab = ({ companyName }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [accountBalances, setAccountBalances] = useState({});
  const [recordsPerPage, setRecordsPerPage] = useState(
    DEFAULT_CLIENT_TRANSACTIONS_PAGE_SIZE
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Load clients
  useEffect(() => {
    if (!companyName) return;

    const clientsRef = ref(database, `Companies/${companyName}/ValidatedClients`);
    const unsubscribe = onValue(clientsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const clientsList = Object.entries(data).map(([key, value]) => ({
          id: key,
          name: value.CloudName || "Unnamed Client",
          phone: value.PhoneNumber || "N/A",
          address: value.Address || "N/A",
          route: value.Route || "N/A",
          balance: value.Balance || 0,
          mode: value.Mode || "Credit",
          unitPrice: value.UnitPrice || 0,
        }));
        setClients(clientsList);
        setLoading(false);
      } else {
        setClients([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Load account balances
  useEffect(() => {
    if (!companyName) return;

    const balancesRef = ref(database, `Companies/${companyName}/AccountBalances`);
    const unsubscribe = onValue(balancesRef, (snapshot) => {
      if (snapshot.exists()) {
        setAccountBalances(snapshot.val() || {});
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Load transactions for selected client
  useEffect(() => {
    if (!selectedClient || !companyName) {
      setTransactions([]);
      return;
    }

    const loadTransactions = async () => {
      try {
        const allTransactions = [];

        // Load purchases
        const purchasesRef = ref(
          database,
          `Companies/${companyName}/ClientPurchases/${selectedClient.id}/purchases`
        );
        const purchasesSnap = await get(purchasesRef);
        if (purchasesSnap.exists()) {
          const data = purchasesSnap.val();
          Object.entries(data).forEach(([year, months]) => {
            Object.entries(months || {}).forEach(([month, items]) => {
              Object.values(items || {}).forEach((purchase) => {
                allTransactions.push({
                  ...purchase,
                  type: "purchase",
                  date: purchase.purchaseDate,
                  amount: purchase.totalAmount,
                });
              });
            });
          });
        }

        // Load payments
        const paymentsRef = ref(
          database,
          `Companies/${companyName}/ClientPayments/${selectedClient.id}/payments`
        );
        const paymentsSnap = await get(paymentsRef);
        if (paymentsSnap.exists()) {
          const data = paymentsSnap.val();
          Object.entries(data).forEach(([year, months]) => {
            Object.entries(months || {}).forEach(([month, items]) => {
              Object.values(items || {}).forEach((payment) => {
                allTransactions.push({
                  ...payment,
                  type: "payment",
                  date: payment.paymentDate || payment.TransactionDate,
                  amount: payment.amount,
                });
              });
            });
          });
        }

        // Load M-Pesa paybill payments (CustomerRecords)
        const customerRecordsRef = ref(
          database,
          `Companies/${companyName}/CustomerRecords/${selectedClient.id}/Payments`
        );
        const customerRecordsSnap = await get(customerRecordsRef);
        if (customerRecordsSnap.exists()) {
          const data = customerRecordsSnap.val();
          Object.entries(data).forEach(([year, months]) => {
            Object.entries(months || {}).forEach(([month, items]) => {
              Object.values(items || {}).forEach((payment) => {
                allTransactions.push({
                  ...payment,
                  type: "mpesa_payment",
                  date: payment.TransactionDate || payment.paymentDate,
                  amount: payment.Amount || payment.amount,
                  reference: payment.TransactionID || payment.transactionRef,
                });
              });
            });
          });
        }

        // Sort by date descending
        allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
        setTransactions(allTransactions);
      } catch (error) {
        console.error("Error loading transactions:", error);
        toast.error("Failed to load transactions");
      }
    };

    loadTransactions();
  }, [selectedClient, companyName]);

  // Filter transactions by date
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    if (dateFilter.startDate && dateFilter.endDate) {
      const start = new Date(dateFilter.startDate);
      const end = new Date(dateFilter.endDate);
      filtered = filtered.filter((t) => {
        const tDate = new Date(t.date);
        return tDate >= start && tDate <= end;
      });
    } else if (dateFilter.month && dateFilter.year) {
      filtered = filtered.filter((t) => {
        const tDate = new Date(t.date);
        return (
          tDate.getMonth() + 1 === parseInt(dateFilter.month) &&
          tDate.getFullYear() === parseInt(dateFilter.year)
        );
      });
    }

    return filtered;
  }, [transactions, dateFilter]);

  // Calculate transaction summary
  const transactionSummary = useMemo(() => {
    const summary = {
      totalPurchases: 0,
      totalPayments: 0,
      netBalance: 0,
      purchaseCount: 0,
      paymentCount: 0,
    };

    filteredTransactions.forEach((t) => {
      if (t.type === "purchase") {
        summary.totalPurchases += parseFloat(t.amount || 0);
        summary.purchaseCount++;
      } else if (t.type === "payment" || t.type === "mpesa_payment") {
        summary.totalPayments += parseFloat(t.amount || 0);
        summary.paymentCount++;
      }
    });

    summary.netBalance = summary.totalPurchases - summary.totalPayments;
    return summary;
  }, [filteredTransactions]);

  const transactionChartData = useMemo(
    () => buildClientTransactionChartData(filteredTransactions),
    [filteredTransactions]
  );
  const totalTransactionPages = useMemo(
    () => getClientTransactionTotalPages(filteredTransactions, recordsPerPage),
    [filteredTransactions, recordsPerPage]
  );
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalTransactionPages);
  const paginatedTransactions = useMemo(
    () => paginateClientTransactions(filteredTransactions, safeCurrentPage, recordsPerPage),
    [filteredTransactions, safeCurrentPage, recordsPerPage]
  );
  const transactionRange = useMemo(
    () =>
      clientTransactionsRangeForPage(
        filteredTransactions,
        safeCurrentPage,
        recordsPerPage
      ),
    [filteredTransactions, safeCurrentPage, recordsPerPage]
  );

  // Filter clients by search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;
    const lower = searchTerm.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.phone.toLowerCase().includes(lower) ||
        c.route.toLowerCase().includes(lower)
    );
  }, [clients, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClient, dateFilter, recordsPerPage]);

  // Export to CSV
  const exportToCSV = () => {
    if (!selectedClient) {
      toast.error("Please select a client first");
      return;
    }

    const headers = [
      "Date",
      "Type",
      "Description",
      "Quantity (L)",
      "Unit Price",
      "Amount (KES)",
      "Payment Method",
      "Balance After",
      "Reference",
    ];

    let runningBalance = 0;
    const rows = filteredTransactions.map((t) => {
      const type = t.type === "purchase" ? "Purchase" : "Payment";
      const description =
        t.type === "purchase"
          ? `Milk Purchase - ${t.paymentMode || "Credit"}`
          : `Payment - ${t.paymentMethod || t.Mode || "Unknown"}`;
      const quantity = t.quantity || 0;
      const unitPrice = t.unitPrice || 0;
      const amount = parseFloat(t.amount || 0);

      if (t.type === "purchase") {
        runningBalance += amount;
      } else {
        runningBalance -= amount;
      }

      return [
        new Date(t.date).toLocaleDateString(),
        type,
        description,
        quantity,
        unitPrice,
        amount.toFixed(2),
        t.paymentMethod || t.paymentMode || t.Mode || "N/A",
        runningBalance.toFixed(2),
        t.reference || t.transactionRef || t.TransactionID || "N/A",
      ];
    });

    const csvContent = [
      `Client Transaction Statement`,
      `Client: ${selectedClient.name}`,
      `Phone: ${selectedClient.phone}`,
      `Period: ${dateFilter.startDate || "All"} to ${dateFilter.endDate || "All"}`,
      `Generated: ${new Date().toLocaleString()}`,
      ``,
      headers.join(","),
      ...rows.map((row) => row.join(",")),
      ``,
      `Summary:`,
      `Total Purchases,${transactionSummary.totalPurchases.toFixed(2)}`,
      `Total Payments,${transactionSummary.totalPayments.toFixed(2)}`,
      `Current Balance,${selectedClient.balance}`,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedClient.name}_transactions_${new Date().getTime()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success("Transaction statement exported successfully");
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!selectedClient) {
      toast.error("Please select a client first");
      return;
    }

    try {
      const doc = new jsPDF();
      
      // Validate that autoTable is available
      if (typeof doc.autoTable !== "function") {
        toast.error("PDF generation library not properly loaded. Please refresh the page.");
        console.error("jspdf-autotable not loaded properly");
        return;
      }
      
      const pageWidth = doc.internal.pageSize.width;
      
      // Title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("CLIENT TRANSACTION STATEMENT", pageWidth / 2, 20, { align: "center" });
      
      // Client Info
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Client: ${selectedClient.name}`, 14, 35);
      doc.text(`Phone: ${selectedClient.phone}`, 14, 40);
      doc.text(`Route: ${selectedClient.route}`, 14, 45);
      doc.text(`Period: ${dateFilter.startDate || "All"} to ${dateFilter.endDate || "All"}`, 14, 50);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 55);
      doc.text(`Company: ${companyName}`, 14, 60);
      
      // Summary Box
      doc.setFillColor(230, 245, 255);
      doc.rect(14, 65, pageWidth - 28, 25, "F");
      doc.setFontSize(9);
      doc.text(`Current Balance: KES ${parseFloat(selectedClient.balance).toLocaleString()}`, 18, 72);
      doc.text(`Total Purchases: KES ${transactionSummary.totalPurchases.toLocaleString()}`, 18, 78);
      doc.text(`Total Payments: KES ${transactionSummary.totalPayments.toLocaleString()}`, 18, 84);
      doc.text(`Net (Period): KES ${transactionSummary.netBalance.toLocaleString()}`, pageWidth - 80, 72);
      doc.text(`Purchase Count: ${transactionSummary.purchaseCount}`, pageWidth - 80, 78);
      doc.text(`Payment Count: ${transactionSummary.paymentCount}`, pageWidth - 80, 84);
      
      // Transactions Table
      const tableData = [];
      let runningBalance = 0;
      
      filteredTransactions.forEach((t) => {
        const type = t.type === "purchase" ? "Purchase" : "Payment";
        const amount = parseFloat(t.amount || 0);
        
        if (t.type === "purchase") {
          runningBalance += amount;
        } else {
          runningBalance -= amount;
        }
        
        tableData.push([
          new Date(t.date).toLocaleDateString(),
          type,
          t.type === "purchase" 
            ? `Milk ${t.quantity || 0}L @ ${t.unitPrice || 0}` 
            : t.paymentMethod || t.Mode || "Payment",
          amount.toFixed(2),
          runningBalance.toFixed(2),
          t.reference || t.transactionRef || t.TransactionID || "-",
        ]);
      });
      
      doc.autoTable({
        startY: 95,
        head: [["Date", "Type", "Description", "Amount (KES)", "Balance", "Reference"]],
        body: tableData,
        theme: "grid",
        headStyles: { fillColor: [6, 182, 212], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 22 },
          2: { cellWidth: 55 },
          3: { cellWidth: 25, halign: "right" },
          4: { cellWidth: 25, halign: "right" },
          5: { cellWidth: 30 },
        },
        margin: { top: 95, left: 14, right: 14 },
      });
      
      // Footer
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(
        `This is a computer-generated statement and does not require a signature.`,
        pageWidth / 2,
        finalY,
        { align: "center" }
      );
      
      // Save PDF
      doc.save(`${selectedClient.name}_transactions_${new Date().getTime()}.pdf`);
      toast.success("PDF statement generated successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  // Client summary cards
  const ClientSummaryCard = ({ client, onClick }) => {
    const balanceColor =
      client.balance < 0
        ? "text-red-600"
        : client.balance > 0
        ? "text-amber-600"
        : "text-green-600";

    return (
      <div
        onClick={() => onClick(client)}
        className="bg-white border rounded-lg p-4 hover:shadow-md transition cursor-pointer"
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <h4 className="font-semibold text-gray-800">{client.name}</h4>
            <p className="text-xs text-gray-500">{client.phone}</p>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${balanceColor}`}>
              KES {parseFloat(client.balance).toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">Balance</p>
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-600">
          <span>{client.route}</span>
          <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded">
            {client.mode}
          </span>
        </div>
      </div>
    );
  };

  // Transaction detail modal
  const TransactionModal = () => {
    if (!showTransactionModal || !selectedClient) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
                <FileText size={24} />
                Transaction History: {selectedClient.name}
              </h3>
              <p className="text-sm text-gray-500">
                Phone: {selectedClient.phone} | Route: {selectedClient.route}
              </p>
            </div>
            <button
              onClick={() => setShowTransactionModal(false)}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-cyan-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Current Balance</p>
                <p className="text-2xl font-bold text-cyan-700">
                  KES {parseFloat(selectedClient.balance).toLocaleString()}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total Purchases</p>
                <p className="text-2xl font-bold text-red-700">
                  KES {transactionSummary.totalPurchases.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {transactionSummary.purchaseCount} transactions
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total Payments</p>
                <p className="text-2xl font-bold text-green-700">
                  KES {transactionSummary.totalPayments.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {transactionSummary.paymentCount} transactions
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Net (Period)</p>
                <p className="text-2xl font-bold text-amber-700">
                  KES {transactionSummary.netBalance.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Date Filter */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="text-cyan-600" size={20} />
                <h4 className="font-semibold text-gray-800">Filter by Date</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, startDate: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, endDate: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Month</label>
                  <select
                    value={dateFilter.month}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, month: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i).toLocaleString("default", {
                          month: "long",
                        })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Year</label>
                  <select
                    value={dateFilter.year}
                    onChange={(e) =>
                      setDateFilter({ ...dateFilter, year: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() =>
                    setDateFilter({
                      startDate: "",
                      endDate: "",
                      month: new Date().getMonth() + 1,
                      year: new Date().getFullYear(),
                    })
                  }
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                >
                  Reset Filter
                </button>
                <label className="px-3 py-1.5 text-sm border rounded bg-white flex items-center gap-2">
                  <span>Records</span>
                  <select
                    value={recordsPerPage}
                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                    className="text-sm bg-transparent focus:outline-none"
                  >
                    {CLIENT_TRANSACTIONS_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={exportToCSV}
                  className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700 flex items-center gap-1"
                >
                  <Download size={16} />
                  Export CSV
                </button>
                <button
                  onClick={exportToPDF}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                >
                  <FileText size={16} />
                  Export PDF
                </button>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-gray-800">Transaction Stats</h4>
              <div className="h-64">
                {transactionChartData.litersByDay.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={transactionChartData.litersByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="liters"
                        name="Liters"
                        stroke="#06b6d4"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500">
                    No purchase liters available for charting.
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Record Types
                  </h5>
                  <div className="h-56">
                    {transactionChartData.recordTypeDistribution.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transactionChartData.recordTypeDistribution}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {transactionChartData.recordTypeDistribution.map((entry, index) => (
                              <Cell
                                key={`record-type-${entry.name}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-gray-500">
                        No records to visualize.
                      </div>
                    )}
                  </div>
                </div>

                <div className="border rounded-lg p-3">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Payment/Record Methods
                  </h5>
                  <div className="h-56">
                    {transactionChartData.methodDistribution.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transactionChartData.methodDistribution}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {transactionChartData.methodDistribution.map((entry, index) => (
                              <Cell
                                key={`record-method-${entry.name}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-sm text-gray-500">
                        No methods to visualize.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-semibold text-gray-800">
                  Transactions ({filteredTransactions.length})
                </h4>
              </div>
              <div className="overflow-x-auto">
                {filteredTransactions.length === 0 ? (
                  <p className="p-8 text-center text-gray-500">
                    No transactions found for the selected period
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Description
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Quantity (L)
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Amount (KES)
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Method
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Reference
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedTransactions.map((t, idx) => {
                        const isPurchase = t.type === "purchase";
                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                              {new Date(t.date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  isPurchase
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {isPurchase ? "Purchase" : "Payment"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {isPurchase
                                ? `Milk Purchase - ${t.paymentMode || "Credit"}`
                                : `Payment - ${
                                    t.paymentMethod || t.Mode || "M-Pesa"
                                  }`}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700">
                              {isPurchase ? (t.quantity || 0).toFixed(2) : "-"}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              <span
                                className={
                                  isPurchase ? "text-red-600" : "text-green-600"
                                }
                              >
                                {isPurchase ? "+" : "-"}
                                {parseFloat(t.amount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {t.paymentMethod ||
                                t.paymentMode ||
                                t.Mode ||
                                "N/A"}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs">
                              {t.reference ||
                                t.transactionRef ||
                                t.TransactionID ||
                                "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {filteredTransactions.length ? (
                <div className="px-4 py-3 border-t bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
                  <p className="text-gray-600">
                    Showing {transactionRange.start}-{transactionRange.end} of{" "}
                    {filteredTransactions.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage((page) => Math.max(1, page - 1))
                      }
                      disabled={safeCurrentPage <= 1}
                      className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Previous
                    </button>
                    <span className="text-gray-700">
                      Page {safeCurrentPage} of {totalTransactionPages}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((page) =>
                          Math.min(totalTransactionPages, page + 1)
                        )
                      }
                      disabled={safeCurrentPage >= totalTransactionPages}
                      className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Next
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Loading clients...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-cyan-700 flex items-center gap-2">
              <Users size={28} />
              Client Payment Transactions
            </h2>
            <p className="text-gray-600 mt-1">
              Track client purchases, payments, and balances
            </p>
          </div>
        </div>

        {/* Account Balances Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Shop Receivables</p>
                <p className="text-2xl font-bold text-cyan-700">
                  KES{" "}
                  {(
                    accountBalances.shop_receivable?.balance || 0
                  ).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Amount owed by clients
                </p>
              </div>
              <TrendingUp className="text-cyan-600" size={32} />
            </div>
          </div>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Company Bank</p>
                <p className="text-2xl font-bold text-green-700">
                  KES{" "}
                  {(accountBalances.company_bank?.balance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Bank balance</p>
              </div>
              <DollarSign className="text-green-600" size={32} />
            </div>
          </div>
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Company Cash</p>
                <p className="text-2xl font-bold text-amber-700">
                  KES{" "}
                  {(accountBalances.company_cash?.balance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Cash on hand</p>
              </div>
              <DollarSign className="text-amber-600" size={32} />
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search clients by name, phone, or route..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Filter size={20} />
          Clients ({filteredClients.length})
        </h3>
        {filteredClients.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No clients found. Add clients to start tracking transactions.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <ClientSummaryCard
                key={client.id}
                client={client}
                onClick={(c) => {
                  setSelectedClient(c);
                  setShowTransactionModal(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Transaction Modal */}
      <TransactionModal />
    </div>
  );
};

export default ClientTransactionsTab;
